import React, { useEffect, useRef, memo } from 'react';
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  text: string;
  size: number;
  highlight: number;
}
export function AnimatedGTLDs() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // List of new gTLDs from the 2015 round
    const gtlds = ['.academy', '.accountant', '.agency', '.apartments', '.associates', '.auction', '.band', '.bargains', '.bike', '.bingo', '.boutique', '.builders', '.business', '.cab', '.cafe', '.camera', '.camp', '.capital', '.cards', '.care', '.careers', '.cash', '.casino', '.catering', '.center', '.cheap', '.church', '.city', '.claims', '.cleaning', '.clinic', '.clothing', '.club', '.coach', '.codes', '.coffee', '.community', '.company', '.computer', '.condos', '.construction', '.consulting', '.contractors', '.cool', '.coupons', '.credit', '.creditcard', '.cruises', '.dance', '.dating', '.deals', '.degree', '.delivery', '.democrat', '.dental', '.diamonds', '.digital', '.direct', '.directory', '.discount', '.doctor', '.dog', '.domains', '.education', '.email', '.energy', '.engineer', '.engineering', '.enterprises', '.equipment', '.estate', '.events', '.exchange', '.expert', '.exposed', '.express', '.fail', '.farm', '.finance', '.financial', '.fish', '.fitness', '.flights', '.florist', '.football', '.forsale', '.foundation', '.fund', '.furniture', '.futbol', '.gallery', '.gifts', '.glass', '.global', '.gold', '.golf', '.graphics', '.gratis', '.gripe', '.guide', '.guru', '.haus', '.healthcare', '.hockey', '.holdings', '.holiday', '.horse', '.house', '.immo', '.immobilien', '.industries', '.institute', '.insure', '.international', '.investments', '.jewelry', '.kitchen', '.land', '.lease', '.legal', '.life', '.lighting', '.limited', '.limo', '.loans', '.maison', '.management', '.market', '.marketing', '.mba', '.media', '.memorial', '.money', '.mortgage', '.movie', '.network', '.news', '.ninja', '.partners', '.parts', '.photo', '.photography', '.photos', '.pictures', '.pizza', '.place', '.plumbing', '.plus', '.productions', '.properties', '.pub', '.recipes', '.reisen', '.rentals', '.repair', '.report', '.republican', '.restaurant', '.reviews', '.rip', '.rocks', '.run', '.sale', '.school', '.services', '.shoes', '.shopping', '.show', '.singles', '.soccer', '.social', '.software', '.solar', '.solutions', '.studio', '.style', '.supplies', '.supply', '.support', '.surgery', '.systems', '.tax', '.taxi', '.technology', '.tennis', '.theater', '.tips', '.tires', '.today', '.tools', '.tours', '.town', '.toys', '.trade', '.training', '.university', '.vacations', '.ventures', '.vet', '.viajes', '.video', '.villas', '.vision', '.voyage', '.watch', '.works', '.world', '.wtf', '.zone'];
    // Resize canvas to match container
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    // Create particles - 3 of each gTLD
    const particles: Particle[] = [];
    const numUniqueGTLDs = Math.min(16, gtlds.length);
    const selectedGTLDs = gtlds.slice(0, numUniqueGTLDs);
    selectedGTLDs.forEach(gtld => {
      for (let i = 0; i < 3; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          text: gtld,
          size: 12 + Math.random() * 8,
          highlight: 0
        });
      }
    });
    // Animation loop
    let animationFrameId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Update and draw particles
      particles.forEach((particle, i) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        // Decay highlight
        if (particle.highlight > 0) {
          particle.highlight -= 0.016;
        }
        // Bounce off walls
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -1;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -1;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }
        // Check overlap with other particles (no physics, just detection)
        for (let j = i + 1; j < particles.length; j++) {
          const other = particles[j];
          const dx = other.x - particle.x;
          const dy = other.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDistance = 50;
          // If overlapping and same gTLD, highlight both
          if (distance < minDistance && particle.text === other.text) {
            particle.highlight = 1;
            other.highlight = 1;
          }
        }
        // Draw particle with color change based on highlight
        const baseOpacity = 0.3;
        const highlightOpacity = 0.9;
        const opacity = baseOpacity + (highlightOpacity - baseOpacity) * particle.highlight;
        // Interpolate between white and red based on highlight value
        const red = 255;
        const green = Math.round(255 * (1 - particle.highlight));
        const blue = Math.round(255 * (1 - particle.highlight));
        ctx.font = `${particle.size}px Arial`;
        ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${opacity})`;
        ctx.fillText(particle.text, particle.x, particle.y);
      });
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{
    pointerEvents: 'none'
  }} data-id="element-55" />;
}